import { t } from '../i18n.ts'
import './DiceControls.css'

const FACES = [1, 2, 3, 4, 5, 6] as const

/**
 * Terningkast — a 1–6 rating, or none.
 *
 * The same shape as `ThumbControls`: a radio group, because the six faces are
 * mutually exclusive, and tapping the active option clears it rather than needing a
 * separate "remove" button that would cost a seventh target on a 390px row.
 *
 * All six faces are on screen at once instead of behind a stepper or a slider. The
 * whole point of a terningkast is that it is one tap after walking out of a concert,
 * with a phone in one hand — and it is the idiom Norwegian readers already know from
 * every review they have ever read, so nothing here needs a legend.
 */
export function DiceControls({
  dice,
  onChange,
}: {
  dice: number | null
  onChange: (dice: number | null) => void
}) {
  function pick(next: number) {
    onChange(dice === next ? null : next)
  }

  return (
    <div className="dice" role="radiogroup" aria-label={t.concert.diceLabel}>
      {FACES.map((face) => (
        <button
          key={face}
          type="button"
          role="radio"
          aria-checked={dice === face}
          className="dice__button"
          data-active={dice === face || undefined}
          onClick={() => pick(face)}
        >
          <DiceIcon value={face} />
          {/*
            The pips are a picture of a number, and a picture is not a name. The
            accessible name comes from here so a screen reader says "Terningkast 4"
            rather than describing a drawing.
          */}
          <span className="sr-only">{t.concert.diceValue(face)}</span>
        </button>
      ))}
    </div>
  )
}

/*
 * Pip geometry on the 24×24 grid the rest of the icons use: three columns at x 8/12/16
 * and three rows at y 8/12/16, inside a die face inset to 3.5. Every face is a subset
 * of those nine positions, which is what makes one component enough for all six.
 */
const PIPS: Record<number, ReadonlyArray<readonly [number, number]>> = {
  1: [[12, 12]],
  2: [
    [8, 8],
    [16, 16],
  ],
  3: [
    [8, 8],
    [12, 12],
    [16, 16],
  ],
  4: [
    [8, 8],
    [16, 8],
    [8, 16],
    [16, 16],
  ],
  5: [
    [8, 8],
    [16, 8],
    [12, 12],
    [8, 16],
    [16, 16],
  ],
  6: [
    [8, 8],
    [16, 8],
    [8, 12],
    [16, 12],
    [8, 16],
    [16, 16],
  ],
}

/**
 * One die face, drawn rather than typed.
 *
 * Shared with the day row (`ConcertRow`), which shows the reader's own rating as an
 * indicator — the list and the control must show the same thing, so they render the
 * same component. Decorative by construction: `aria-hidden`, with whatever names it
 * left to the caller's own `sr-only` text.
 *
 * The 18px intrinsic size matches `ThumbUpIcon`, which is the size the day row wants;
 * the concert page's larger targets scale it from CSS.
 */
export function DiceIcon({ value }: { value: number }) {
  const pips = PIPS[value] ?? []
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect
        x="3.5"
        y="3.5"
        width="17"
        height="17"
        rx="4.5"
        stroke="currentColor"
        strokeWidth="1.9"
      />
      {pips.map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.7" fill="currentColor" />
      ))}
    </svg>
  )
}
